// frontend/src/hooks/useZones.ts — the zones read surface (list · detail · the
// per-zone responsibility chains), mirroring useRoles. Project-scoped server state
// (FRONTEND-PATTERNS §State): list + detail share the `['zones', projectId, …]`
// prefix; the accountability chains live under their OWN key so a chain edit
// doesn't refetch the zone detail's StatStrip.
//
// The list answers under a named rule — `manage` (the default; this hook's list: the
// admin page's two audiences, an admin or a vocabulary manager seeing every zone, a
// people manager their own-scope zones) · `reach` (a filter's source: any member, the
// zones their scope reaches — the naming rule). The chains route additionally needs
// has_field_force — the consumer only mounts the chains query when the module is on.
// 401 is the centralized apiClient's job (ADR-003); errors bubble so the 5-state
// contract renders.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ContainerListRule } from '@ems-app/schemas/zones/requests';
import type { ZoneDetail, ZoneListItem, ZonesListResponse } from '@ems-app/schemas/zones/responses';
import { apiClient } from '../lib/apiClient.js';
import type { SmallSource } from './useOptions.js';

export const zonesKeys = {
  /** List key (the `manage` rule — the admin page's list) — also the invalidation PREFIX (covers detail + reach). */
  all: (projectId: string) => ['zones', projectId] as const,
  /** The list under the `reach` rule — a filter's source; under the prefix, so the zone mutations invalidate it too. */
  reach: (projectId: string) => ['zones', projectId, 'reach'] as const,
  detail: (projectId: string, zoneId: number) => ['zones', projectId, zoneId] as const,
};

// ── list ────────────────────────────────────────────────────────────────────
async function fetchZones(projectId: string, rule: ContainerListRule): Promise<ZonesListResponse> {
  const r = await apiClient.get<ZonesListResponse>(`/projects/${projectId}/zones`, { params: { rule } });
  return r.data;
}

// Module-level selector (referentially stable, §State select doctrine).
const selectItems = (r: ZonesListResponse): ZoneListItem[] => r.items;

export function useZones(projectId: string): UseQueryResult<ZoneListItem[], Error> {
  return useQuery({
    queryKey: zonesKeys.all(projectId),
    queryFn: () => fetchZones(projectId, 'manage'),
    enabled: projectId.length > 0,
    select: selectItems,
  });
}

/** The zones as a picker's source (ADR-080 §3 — the small kind): the WHOLE list on the press, a minute fresh,
 *  invalidated by the zone mutations; a disabled zone is an inactive row. Under the `manage` rule (the default — a
 *  FORM on an admin page: the cluster's parent zone) it shares this hook's own list key, so the admin page and the
 *  picker are one cache entry; under `reach` (a FILTER — any member, the zones their scope reaches: the naming rule)
 *  it keeps its own key under the same prefix. */
export function zonesSource(projectId: string, rule: ContainerListRule = 'manage'): SmallSource<ZonesListResponse> {
  return {
    kind: 'small',
    queryKey: rule === 'reach' ? zonesKeys.reach(projectId) : zonesKeys.all(projectId),
    fetch: () => fetchZones(projectId, rule),
    rows: (r) => r.items.map((z) => ({ id: String(z.id), label: z.name, pickable: true, inactive: z.is_disabled, group: null })),
  };
}

// ── detail ────────────────────────────────────────────────────────────────────
/** The zone's detail read. `quiet` — a form's Load the latest: a zone gone meanwhile answers the form, never the
 *  not-found page (ADR-084 §6). */
export async function fetchZone(projectId: string, zoneId: number, quiet = false): Promise<ZoneDetail> {
  const r = await apiClient.get<ZoneDetail>(`/projects/${projectId}/zones/${zoneId}`, quiet ? { quietNotFound: true } : undefined);
  return r.data;
}

export function useZone(
  projectId: string,
  zoneId: number | null,
): UseQueryResult<ZoneDetail, Error> {
  return useQuery({
    queryKey: zonesKeys.detail(projectId, zoneId ?? 0),
    queryFn: () => fetchZone(projectId, zoneId as number),
    enabled: projectId.length > 0 && zoneId != null && zoneId > 0,
  });
}

